import { Controller } from "@hotwired/stimulus"
import debounce from "lodash.debounce"

const DEBOUNCE_TIMING = 600

export default class extends Controller {
  static targets = ["filterForm", "perPageInput", "filterFormInside"]

  static values = {
    loading: { type: Boolean, default: false },
    shouldUpdateByChangingFrameSrc: { type: Boolean, default: false },
    refreshFiltersOnChange: { type: Boolean, default: false },
  }

  connect() {
    this.id = this.element.id

    this.frame = this.element.querySelector("turbo-frame")

    this.boundHandleContentFade = this.handleContentFade.bind(this)
    this.boundHandleReloadEvent = this.handleReloadEvent.bind(this)

    this.frame.addEventListener(
      "turbo:before-frame-render",
      this.boundHandleContentFade,
    )

    this.debouncedTurboVisit = debounce(
      (href, options = {}) => Turbo.visit(href, options),
      DEBOUNCE_TIMING,
    )

    this.debouncedSubmitFilterForm = debounce(
      () => this.submitFilterForm(),
      DEBOUNCE_TIMING,
    )

    document.addEventListener("ui:datagrid:reload", this.boundHandleReloadEvent)
  }

  disconnect() {
    this.frame.removeEventListener(
      "turbo:before-frame-render",
      this.boundHandleContentFade,
    )

    document.removeEventListener("ui:datagrid:reload", this.boundHandleReloadEvent)
  }

  triggerLoading() {
    if (this.loadingValue) { return }

    this.loadingValue = true

    const datagrid = this.element
    const tableBody = datagrid.querySelector("tbody")
    const rows = tableBody.querySelectorAll("tr")
    const template = datagrid.querySelector("template")

    tableBody.classList.add("loading")
    tableBody.classList.remove("loaded")

    rows.forEach((row) => {
      row.classList.add("collapse")
    })

    tableBody.innerHTML += template.innerHTML
  }

  handleContentFade(event) {
    const oldFrame = this.frame
    const newFrame = event.detail.newFrame

    if (oldFrame.id !== newFrame.id) {
      return
    }

    const oldContent = oldFrame.querySelector("tbody")

    if (oldContent) {
      oldContent.classList.add("fade-out")
    }

    const newContent = newFrame.querySelector("tbody") || newFrame
    newContent.classList.add("fade-in")

    this.loadingValue = false
  }

  sortColumns(e) {
    e.preventDefault()

    this.triggerLoading()

    const turboFrame = this.frame
    const turboFrameId = turboFrame.id

    if (this.shouldUpdateByChangingFrameSrcValue) {
      const url = e.currentTarget.href

      if (turboFrame) {
        setTimeout(() => {
          turboFrame.src = url
        }, 250)
      }
    } else {
      this.debouncedTurboVisit(e.currentTarget.href, { frame: turboFrameId })
    }
  }

  changeFilters(e) {
    if (e.target.dataset.disableAutoSubmit == "true") {
      return
    }

    if (
      e.target.form == this.filterFormTarget ||
      e.target.dataset.formId === this.filterFormTarget.id
    ) {
      if (this.refreshFiltersOnChangeValue) {
        this.refreshFilters()
      }

      this.triggerLoading()

      this.debouncedSubmitFilterForm()
    }
  }

  submitFilterForm() {
    if (this.shouldUpdateByChangingFrameSrcValue) {
      this.updateFrameSource()
    } else {
      this.filterFormTarget.requestSubmit()
    }
  }

  updateFrameSource() {
    const form = this.filterFormTarget
    const formData = new FormData(form)
    const params = new URLSearchParams(formData)
    const baseUrl = form.action || window.location.pathname
    const newUrl = `${baseUrl}?${params.toString()}`

    const turboFrame = this.frame

    if (turboFrame) {
      turboFrame.src = newUrl
    }
  }

  changePerPage(e) {
    const newValue = e.target.value

    if (newValue != this.perPageInputTarget.value) {
      this.perPageInputTarget.value = newValue
      this.perPageInputTarget.removeAttribute("disabled")

      this.triggerLoading()
      this.debouncedSubmitFilterForm()
    }
  }

  selectionsChanged(e) {
    this.dispatch("selectionsChanged", { detail: e.detail })
  }

  refreshFilters(e) {
    if (this.hasFilterFormInsideTarget) {
      this.filterFormInsideTarget.removeAttribute("data-turbo-permanent")
    }
  }

  handleReloadEvent(event) {
    if (event.detail.id == this.id) {
      this.updateFrameSource()
    }
  }
}
